import { useQuery } from '@tanstack/react-query';
import ms from 'ms';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import { Loader } from '@hivebrite/design-system/Loader';

import PageCustomizable from 'hb-react/frontoffice/components/PageCustomizable';
import ErrorBoundary from 'hb-react/shared/components/ErrorBoundary';
import LaunchDarklyWrapper from 'hb-react/shared/components/LaunchDarklyWrapper';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

const AsyncPageCustomizableContainer = ({ url, ...props }) => {
  const { isPreview, page_locale: pageLocale, redirectUrl } = props;

  const { data: page } = useQuery({
    queryKey: [`page-customizable`, url],
    queryFn: async () => {
      try {
        const response = await axios.get(`${url}.json`, {
          params: {
            draft: isPreview,
            page_locale: pageLocale,
            native_page: false,
          },
        });

        return response.data.page_customizable;
      } catch (error) {
        const statusCode = error.response?.status;

        if (statusCode >= 400 && statusCode < 500 && redirectUrl) {
          // Page can be rendered outside of react router.
          window.location.href = redirectUrl;
        }

        throw error;
      }
    },
    staleTime: ms('5m'),
  });

  if (!page) {
    return <Loader size={50} />;
  }

  return <PageCustomizable key={page.url} {...page} {...props} />;
};

const StaticPageCustomizableContainer = ({ page, ...props }) => {
  const { page_customizable: pc } = page;

  return <PageCustomizable {...pc} {...props} />;
};

const PageCustomizableContainer = (props) => {
  const { page } = props;

  useEffect(() => {
    if (!window.location.hash) {
      return;
    }

    let observer;
    const elementId = window.location.hash.substring(1);

    const scrollToElement = () => {
      const targetElement = document.getElementById(elementId);

      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
        if (observer) {
          observer.disconnect();
        }
      }
    };

    scrollToElement();

    if (!document.getElementById(elementId)) {
      observer = new MutationObserver(scrollToElement);
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [window.location]);

  const Container = page
    ? StaticPageCustomizableContainer
    : AsyncPageCustomizableContainer;

  return (
    <ErrorBoundary>
      <LaunchDarklyWrapper>
        <Container {...props} />
      </LaunchDarklyWrapper>
    </ErrorBoundary>
  );
};

PageCustomizableContainer.propTypes = {
  url: PropTypes.string,
  withPageTitle: PropTypes.bool,
};

export default PageCustomizableContainer;
